// Часть разбиения MapView.vue (фаза 2) - отрисовка выбранного маршрута
// (линия/маркеры старт-финиш/точки маршрута), плюс попапы с погодой
// ближайшей станции для всех трёх видов маркеров.
//
// syncCursorFromProp/removeCursorMarker принимаются параметрами из
// useRouteCursor.ts (создаётся раньше, см. MapView.vue) - обратной
// зависимости нет: useRouteCursor читает состояние этого composable только
// через геттеры (getCurrentRoutePointsLLE/getCursorCumulativeKm/
// getCurrentRouteProfilePoints), возвращаемые ниже.
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import type { Feature } from "geojson";
import api from "../api";
import type { RouteMapItem } from "../types/routes";
import { DEFAULT_ROUTE_STYLE, normalizeRouteStyle } from "../utils/routeLineStyle";
import type { PointLLE, RouteStyleConfig } from "../utils/routeLineStyle";
import {
  ROUTE_ARROW_IMAGE_ID,
  buildArrowIconImage,
  buildRouteLineFeatures,
  buildRouteLineLayout,
  buildRouteLinePaint,
} from "../utils/routeLineStyleMaplibre";
import { buildCumulativeKm } from "../utils/routeCursor";
import type { ElevationProfilePoint } from "../utils/routeCursor";
import { ROUTE_SOURCES } from "../map/constants";
import type { MapCtx } from "../map/mapContext";
import {
  createRouteBadgeElement,
  createRouteFinishFlagElement,
  createRouteStartDotElement,
  escapeHtml,
  formatMetric,
  formatPercent,
  setGeoJsonSourceData,
  setMarkerVisible,
  shouldShowDetailMarkers,
  upsertPopupContent,
} from "../map/mapUiHelpers";
import warnIconUrl from "../assets/poi-icons/warn.png";
import waterIconUrl from "../assets/poi-icons/water.png";
import campIconUrl from "../assets/poi-icons/camp.png";
import { POI_TYPE_MARKERS } from "../map/poiTypeMarkers";
import { isPoiType } from "../types/poiTypes";
import { safeStringify } from "./useMapDebugPanel";

const normalizePoiType = (value: string | null | undefined) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

// Сначала точное совпадение с одним из 12 канонических ключей
// (types/poiTypes.ts, редактор маршрутов всегда пишет их) - цвет/глиф из
// общей таблицы map/poiTypeMarkers.ts. Substring-эвристика ниже остаётся
// фолбэком только для legacy свободнотекстового poi_type из старых
// GPX-импортов, которые этим ключам не соответствуют.
const getPoiMarkerKind = (poi: any) => {
  const raw = String(poi?.poi_type ?? "").trim();
  if (isPoiType(raw)) {
    const marker = POI_TYPE_MARKERS[raw];
    return { variant: marker.variant, glyph: marker.glyph, icon: marker.icon, label: marker.label };
  }
  const normalized = normalizePoiType(poi?.poi_type);
  if (normalized.includes("ноч") || normalized.includes("camp") || normalized.includes("night-stop")) {
    return { variant: "camp", glyph: "⛺", icon: campIconUrl, label: "Ночёвка" };
  }
  if (normalized.includes("вод") || normalized.includes("water")) {
    return { variant: "water", glyph: "💧", icon: waterIconUrl, label: "Вода" };
  }
  if (normalized.includes("опас") || normalized.includes("danger") || normalized.includes("hazard")) {
    return { variant: "danger", glyph: "⚠", icon: warnIconUrl, label: "Опасный участок" };
  }
  return { variant: "default", glyph: "•", icon: null as string | null, label: "Точка маршрута" };
};

// Безымянные GPX-вейпоинты получают на бэкенде заглушку "Точка N"/"Маркер N"
// (build_poi_payload в backend/app/routes/geometry.py) - в заголовке попапа
// лучше показать тип точки ("Ночёвка" читается лучше, чем "Точка 3").
const GENERIC_POI_NAME_RE = /^(Точка|Маркер)\s+\d+$/i;

const poiDisplayTitle = (poi: any, label: string) => {
  const name = String(poi?.name ?? "").trim();
  return name && !GENERIC_POI_NAME_RE.test(name) ? name : label;
};

const formatStationDistance = (distanceKm: number | null | undefined) =>
  typeof distanceKm === "number" ? `${distanceKm.toFixed(1)} км` : "—";

const makePoiNearestStationHtml = (
  station: any | null | undefined,
  options: { loading?: boolean } = {}
) => {
  if (options.loading) {
    return `
      <div class="route-poi-popup__weather route-poi-popup__weather--loading">
        <div class="route-poi-popup__weather-label">Погода</div>
        <div class="route-poi-popup__weather-empty">Загружаем данные…</div>
      </div>
    `;
  }

  if (!station) {
    return "";
  }

  const latest = station.latest;
  return `
    <div class="route-poi-popup__weather">
      <div class="route-poi-popup__weather-label">Погода</div>
      <div class="route-poi-popup__weather-head">
        <div class="route-poi-popup__weather-name-row">
          <span class="route-poi-popup__weather-name">${escapeHtml(station.node_name || station.name)}</span>
          <span class="route-poi-popup__weather-distance"><span class="route-poi-popup__weather-distance-icon">↗</span>${formatStationDistance(station.distance_km)}</span>
        </div>
      </div>
      <div class="route-poi-popup__weather-metrics">
        <span>🌡 ${formatMetric(latest?.temp_c, "°C")}</span>
        <span>🌬 ${formatMetric(latest?.wind_speed_ms, " м/с", 1)}</span>
        <span>💧 ${formatPercent(latest?.humidity_pct)}</span>
      </div>
    </div>
  `;
};

const makeStationLookupKey = (lat: number, lng: number) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

const makeRoutePointPopupHtml = (
  title: string,
  latitude: number,
  longitude: number,
  description: string | null | undefined,
  nearestStation?: any | null,
  loadingStation = false
) => {
  const coordsText = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
  return `
    <div class="route-poi-popup">
      <div class="route-poi-popup__title">${escapeHtml(title)}</div>
      <button
        type="button"
        class="route-poi-popup__coords"
        data-copy-coords="${escapeHtml(coordsText)}"
        title="Скопировать координаты"
      >
        <span class="route-poi-popup__coords-text">${escapeHtml(coordsText)}</span>
        <span class="route-poi-popup__coords-icon" aria-hidden="true">📋</span>
      </button>
      ${description ? `<div class="route-poi-popup__description">${escapeHtml(description)}</div>` : ""}
      ${makePoiNearestStationHtml(nearestStation, { loading: loadingStation })}
    </div>
  `;
};

const makeRouteEndpointPopupHtml = (
  label: string,
  coords: [number, number],
  nearestStation?: any | null,
  loadingStation = false
) => makeRoutePointPopupHtml(label, coords[0], coords[1], null, nearestStation, loadingStation);

const makePoiPopupHtml = (poi: any, nearestStation?: any | null, loadingStation = false) => {
  const { label } = getPoiMarkerKind(poi);
  return makeRoutePointPopupHtml(
    poiDisplayTitle(poi, label),
    poi.latitude,
    poi.longitude,
    poi.description,
    nearestStation,
    loadingStation
  );
};

export interface UseRouteDrawingOptions {
  getCursorDistanceKmProp: () => number | null | undefined;
  syncCursorFromProp: (km: number | null | undefined) => void;
  removeCursorMarker: () => void;
}

export function useRouteDrawing(ctx: MapCtx, options: UseRouteDrawingOptions) {
  // Геометрия подгружается только для выбранного маршрута (loadSelectedRoute) -
  // GET /routes больше не отдаёт геометрию для всех маршрутов сразу (стало
  // медленно на 100+ маршрутах, см. backend/app/routes/router.py).
  let currentRouteId: number | null = null;
  let currentRoutePointsLLE: PointLLE[] = [];
  let routeStyleConfig: RouteStyleConfig = DEFAULT_ROUTE_STYLE;
  let currentRoutePoiMarkers: Marker[] = [];
  let currentRouteEndpointMarkers: Marker[] = [];
  let currentRouteStart: [number, number] | null = null; // [долгота, широта]
  let routeLoadToken = 0; // защита от устаревшего запроса, добивающего свежий выбор
  let cursorCumulativeKm: number[] = [];
  // Для текста высоты/уклона в тултипе перетаскивания - те же данные, что
  // читает график высот (RouteMapItem.elevation_profile).
  let currentRouteProfilePoints: ElevationProfilePoint[] = [];
  const poiStationCache = new Map<string, any | null>();

  const loadNearestStationForCoordinates = async (
    marker: Marker,
    renderPopup: (station?: any | null, loading?: boolean) => string,
    latitude: number,
    longitude: number
  ) => {
    const cacheKey = makeStationLookupKey(latitude, longitude);
    if (poiStationCache.has(cacheKey)) {
      upsertPopupContent(marker, renderPopup(poiStationCache.get(cacheKey)), ctx);
      return;
    }

    upsertPopupContent(marker, renderPopup(null, true), ctx);

    try {
      const res = await api.get("/stations/nearby", {
        params: { lat: latitude, lng: longitude, radius_km: 7, limit: 1 },
      });
      const nearestStation = Array.isArray(res.data) ? res.data[0] ?? null : null;
      poiStationCache.set(cacheKey, nearestStation);
      upsertPopupContent(marker, renderPopup(nearestStation), ctx);
    } catch {
      poiStationCache.set(cacheKey, null);
      upsertPopupContent(marker, renderPopup(null), ctx);
    }
  };

  const loadNearestStationForPoi = async (poi: any, marker: Marker) => {
    if (typeof poi.latitude !== "number" || typeof poi.longitude !== "number") return;

    await loadNearestStationForCoordinates(
      marker,
      (station, loading) => makePoiPopupHtml(poi, station, loading),
      poi.latitude,
      poi.longitude
    );
  };

  const setRouteSourceData = (id: string, features: Feature[]) => setGeoJsonSourceData(ctx.getMap(), id, features);

  const clearCurrentRoute = () => {
    Object.values(ROUTE_SOURCES).forEach((id) => setRouteSourceData(id, []));
    currentRoutePointsLLE = [];
    cursorCumulativeKm = [];
    currentRouteProfilePoints = [];
    options.removeCursorMarker();
    currentRoutePoiMarkers.forEach((marker) => marker.remove());
    currentRouteEndpointMarkers.forEach((marker) => marker.remove());
    currentRoutePoiMarkers = [];
    currentRouteEndpointMarkers = [];
    currentRouteStart = null;
    currentRouteId = null;
  };

  // Paint/layout живут на слоях, а не в данных, поэтому смена стиля (переключение
  // admin-пресета или запоздавший начальный /route-style/active) должна
  // протолкнуть их явно - включая перерастеризацию иконки стрелки, чей цвет и
  // форма тоже часть конфига.
  const applyRouteLineStyle = () => {
    const map = ctx.getMap();
    if (!map || !ctx.isMapReady()) return;
    const paint = buildRouteLinePaint(routeStyleConfig);
    const layout = buildRouteLineLayout(routeStyleConfig);
    const byLayer: Record<string, Record<string, unknown>> = {
      [ROUTE_SOURCES.glow]: paint.glow,
      [ROUTE_SOURCES.casing]: paint.casing,
      [ROUTE_SOURCES.main]: paint.main,
    };
    for (const [layerId, properties] of Object.entries(byLayer)) {
      for (const [name, value] of Object.entries(properties)) {
        map.setPaintProperty(layerId, name as any, value as any);
      }
      for (const [name, value] of Object.entries(layout)) {
        map.setLayoutProperty(layerId, name as any, value as any);
      }
    }
    const icon = buildArrowIconImage(routeStyleConfig.arrows);
    const image = { width: icon.width, height: icon.height, data: icon.data };
    if (map.hasImage(ROUTE_ARROW_IMAGE_ID)) {
      map.updateImage(ROUTE_ARROW_IMAGE_ID, image);
    } else {
      map.addImage(ROUTE_ARROW_IMAGE_ID, image, { pixelRatio: 2 });
    }
  };

  // Перерисовывает только линию (casing/градиент/glow/стрелки) текущего
  // маршрута, не трогая маркеры и вьюпорт - используется и в drawRoute() ниже,
  // и когда запоздавший /route-style/active приходит для маршрута, уже
  // показанного на экране.
  const redrawCurrentRouteLine = () => {
    if (!ctx.getMap() || !ctx.isMapReady() || currentRoutePointsLLE.length === 0) return;
    applyRouteLineStyle();
    const features = buildRouteLineFeatures(currentRoutePointsLLE, routeStyleConfig);
    setRouteSourceData(ROUTE_SOURCES.glow, features.glow);
    setRouteSourceData(ROUTE_SOURCES.casing, features.casing);
    setRouteSourceData(ROUTE_SOURCES.main, features.main);
    setRouteSourceData(ROUTE_SOURCES.arrows, features.arrows);
  };

  // Рисует только один выбранный маршрут - состояния «загружен, но не
  // активен» больше нет (см. комментарий у currentRouteId), поэтому всегда
  // используется вес/прозрачность «активного» стиля напрямую.
  const drawRoute = (route: RouteMapItem) => {
    const map = ctx.getMap();
    if (!map || !ctx.isMapReady()) return;
    const rawCoords = (route.coordinates || []) as number[][];
    if (rawCoords.length === 0) return;
    const pointsWithElevation: PointLLE[] = rawCoords.map((c) => [
      c[1],
      c[0],
      typeof c[2] === "number" ? c[2] : null,
    ]);
    currentRoutePointsLLE = pointsWithElevation;
    // Считается один раз на маршрут - обе операции курсора (проекция клика и
    // разрешение км->lnglat) переиспользуют это вместо повторного обхода трека.
    cursorCumulativeKm = buildCumulativeKm(pointsWithElevation);
    currentRouteProfilePoints = route.elevation_profile?.points ?? [];
    redrawCurrentRouteLine();
    // Курсор мог быть выставлен ещё до загрузки маршрута - расставляем его.
    options.syncCursorFromProp(options.getCursorDistanceKmProp());

    const first = pointsWithElevation[0];
    const last = pointsWithElevation[pointsWithElevation.length - 1];
    const startCoords: [number, number] = [first[0], first[1]]; // [широта, долгота] - порядок для попапа
    const endCoords: [number, number] = [last[0], last[1]];
    currentRouteStart = [first[1], first[0]];

    const startMarker = new maplibregl.Marker({
      element: createRouteStartDotElement(`Старт · ${route.name || "Маршрут"}`),
      anchor: "center",
    }).setLngLat([first[1], first[0]]);
    upsertPopupContent(startMarker, makeRouteEndpointPopupHtml("Старт", startCoords), ctx, () => {
      loadNearestStationForCoordinates(
        startMarker,
        (station, loading) => makeRouteEndpointPopupHtml("Старт", startCoords, station, loading),
        startCoords[0],
        startCoords[1]
      );
    });
    startMarker.addTo(map);
    currentRouteEndpointMarkers.push(startMarker);

    const isLoopRoute = startCoords[0] === endCoords[0] && startCoords[1] === endCoords[1];
    if (!isLoopRoute) {
      const endMarker = new maplibregl.Marker({
        element: createRouteFinishFlagElement(`Финиш · ${route.name || "Маршрут"}`),
        anchor: "bottom-left",
      }).setLngLat([last[1], last[0]]);
      upsertPopupContent(endMarker, makeRouteEndpointPopupHtml("Финиш", endCoords), ctx, () => {
        loadNearestStationForCoordinates(
          endMarker,
          (station, loading) => makeRouteEndpointPopupHtml("Финиш", endCoords, station, loading),
          endCoords[0],
          endCoords[1]
        );
      });
      endMarker.addTo(map);
      currentRouteEndpointMarkers.push(endMarker);
    }

    const showPois = shouldShowDetailMarkers(map);
    (route.pois || []).forEach((poi: any) => {
      if (typeof poi.latitude !== "number" || typeof poi.longitude !== "number") return;
      const { variant, glyph, icon, label } = getPoiMarkerKind(poi);
      const marker = new maplibregl.Marker({
        element: createRouteBadgeElement(variant, glyph, poiDisplayTitle(poi, label), icon),
        anchor: "bottom",
      }).setLngLat([poi.longitude, poi.latitude]);
      upsertPopupContent(marker, makePoiPopupHtml(poi), ctx, () => {
        loadNearestStationForPoi(poi, marker);
      });
      marker.addTo(map!);
      setMarkerVisible(marker, showPois);
      currentRoutePoiMarkers.push(marker);
    });

    currentRouteId = route.id;
    if (currentRouteStart) {
      map.jumpTo({ center: currentRouteStart, zoom: 14 });
    }
  };

  const loadSelectedRoute = async (routeId: number) => {
    const token = ++routeLoadToken;
    try {
      const res = await api.get<RouteMapItem>(`/routes/${routeId}`);
      if (token !== routeLoadToken || !ctx.isMapReady()) return; // подоспел более новый выбор
      clearCurrentRoute();
      drawRoute(res.data);
    } catch (err) {
      ctx.pushDebug("fetch route detail failed", safeStringify(err));
    }
  };

  const applyRouteZoomVisibility = () => {
    const showPois = shouldShowDetailMarkers(ctx.getMap());
    currentRoutePoiMarkers.forEach((marker) => setMarkerVisible(marker, showPois));
  };

  const loadRouteStyleConfig = async () => {
    try {
      const res = await api.get<RouteStyleConfig>("/route-style/active");
      routeStyleConfig = normalizeRouteStyle(res.data);
      // Маршрут может уже быть на экране (диплинк ?route= или отрисован до
      // ответа этого запроса) - перестилизуем на месте, не двигая вьюпорт.
      redrawCurrentRouteLine();
    } catch (err) {
      ctx.pushDebug("fetch route style failed, using defaults", safeStringify(err));
    }
  };

  // Используется только при полном размонтировании MapView (см. onBeforeUnmount) -
  // повторяет исходный ручной сброс, а не clearCurrentRoute() целиком (тот
  // ещё и трогает источники карты через setRouteSourceData, что не нужно,
  // когда сама карта следующей строкой уничтожается через map.remove()).
  const resetForUnmount = () => {
    currentRoutePoiMarkers.forEach((marker) => marker.remove());
    currentRouteEndpointMarkers.forEach((marker) => marker.remove());
    currentRoutePoiMarkers = [];
    currentRouteEndpointMarkers = [];
    poiStationCache.clear();
    currentRoutePointsLLE = [];
    currentRouteId = null;
  };

  return {
    clearCurrentRoute,
    redrawCurrentRouteLine,
    applyRouteLineStyle,
    drawRoute,
    loadSelectedRoute,
    applyRouteZoomVisibility,
    loadRouteStyleConfig,
    resetForUnmount,
    getRouteStyleConfig: () => routeStyleConfig,
    getCurrentRoutePointsLLE: () => currentRoutePointsLLE,
    getCursorCumulativeKm: () => cursorCumulativeKm,
    getCurrentRouteProfilePoints: () => currentRouteProfilePoints,
  };
}
